import { Alert } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { CompanyActiveState, ConfirmationDialogResult, EnumHelper } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import { SaveDataObj } from "../../../framework/saveData";
import { CompanyListItem, ICompanyListItem } from "../../../models/company/companyListItem";
import * as companyService from "../../../models/services/companyService";
import { ActionContainer } from "../../framework/ActionContainer";
import { List } from "../../framework/List/List";
import { ListIconButtonsItem } from "../../framework/List/ListIconButtons/ListIconButtonsItem/ListIconButtonsItem";
import { ListSearchFilter } from "../../framework/List/ListSearchFilter";
import { EllipsisTypography } from "../../framework/Typography/EllipsisTypography";
import { SelectCompanyDialog } from "../SelectCompanyDialog/SelectCompanyDialog";
import { VerticalContainer } from "ui/Scripts/source/components/framework/VerticalContainer";

export interface ICompanyEditPartnerCompanyListProp {
    isReadOnly: boolean;
    companyId: string;
    allPartnersCompanyGroupId: string;
    companies: ICompanyListItem[];
    onRemoved: (ids: string[]) => void;
    onAdded: (companyGroups: ICompanyListItem[]) => void;
}

interface ICompanyEditPartnerCompanyListState {
    // List
    isLoading: boolean;
    companies: ICompanyListItem[];
    filter: string;
    sortColumn: string;
    sortOrderIsAsc: boolean;
    selectedId: string;
    checkedIds: string[];

    // Dialog
    showSelectCompanyDialog: boolean;
}

export class CompanyEditPartnerCompanyList extends React.Component<ICompanyEditPartnerCompanyListProp, ICompanyEditPartnerCompanyListState> {
    constructor(props: ICompanyEditPartnerCompanyListProp) {
        super(props);
        this.state = {
            isLoading: false,
            companies: props.companies ? props.companies.slice(0) : [],
            filter: "",
            sortColumn: "name",
            sortOrderIsAsc: true,
            selectedId: null,
            checkedIds: [],
            showSelectCompanyDialog: false,
        };
    }

    filteredList = (filter: string, column: string, asc: boolean) => {
        const props = this.props;
        let companies = props.companies ? props.companies.slice(0) : [];
        if (filter) {
            const lowFilter = filter.toLowerCase();
            companies = companies.filter(i => i.code.toLowerCase().indexOf(lowFilter) > -1 || i.name.toLowerCase().indexOf(lowFilter) > -1);
        }
        CompanyListItem.sortCompanyListItems(companies, column, asc);
        this.setState({
            filter,
            sortColumn: column,
            sortOrderIsAsc: asc,
            companies
        });
    };

    componentDidUpdate(prevProps: ICompanyEditPartnerCompanyListProp, prevState: ICompanyEditPartnerCompanyListState) {
        const props = this.props;
        const state = this.state;
        if (Base.equalStrArrays(prevProps.companies ? prevProps.companies.map(i => i.id) : [], props.companies ? props.companies.map(i => i.id) : [])) return;
        this.filteredList(state.filter, state.sortColumn, state.sortOrderIsAsc);
    };

    changeFilter = (filter: string) => {
        const state = this.state;
        this.filteredList(filter, state.sortColumn, state.sortOrderIsAsc);
    };

    handleSearchLines = (sortColumn: string, sortOrderIsAsc: boolean, nextPage: boolean) => {
        const state = this.state;
        this.filteredList(state.filter, sortColumn, sortOrderIsAsc);
    };

    handleSetSelectedIds = (selectedId: string, checkedIds: string[]) => {
        this.setState({
            selectedId,
            checkedIds
        });
    };

    getRemoveSaveData = (props: ICompanyEditPartnerCompanyListProp, companyIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyId);
        result.append("companyIds", companyIds);
        return result;
    };

    handleRemove = async () => {
        const props = this.props;
        const state = this.state;
        const selectedIds = List.getSelectedIds(state.selectedId, state.checkedIds);
        if (selectedIds.length < 1) return;
        const companyNames = state.companies.filter(i => selectedIds.indexOf(i.id) > -1).map(i => i.name);
        if (await AppUtils.showConfirmationDialog(String.format(selectedIds.length < 2
            ? Translations.DoYouReallyWantToRemovePartnerParameter
            : Translations.DoYouReallyWantToRemovePartnersParameter, Base.getStringWithSeparators(companyNames, ", "))) === ConfirmationDialogResult.Yes) {
            const saveData = this.getRemoveSaveData(props, selectedIds);
            // Call server
            const result = await AppUtils.callService(() => companyService.companyEditRemovePartnerCompanies(saveData.obj));
            if (!result) return;
            props.onRemoved(selectedIds);
        }
    };
    
    handleAdd = () => {
        this.setState({
            showSelectCompanyDialog: true
        });
    };

    getAddSaveData = (props: ICompanyEditPartnerCompanyListProp, companyIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.companyId);
        result.append("companyIds", companyIds);
        return result;
    };

    onSelectCompanyDialogOk = async (companies: ICompanyListItem[]) => {
        if (!companies || companies.length < 1) return;
        this.setState({
            showSelectCompanyDialog: false
        });
        const saveData = this.getAddSaveData(this.props, companies.map(i => i.id));
        // Call server
        const result = await AppUtils.callService(() => companyService.companyEditAddPartnerCompanies(saveData.obj));
        if (!result) return;
        this.props.onAdded(companies);
    };

    onSelectCompanyDialogCancel = () => {
        this.setState({
            showSelectCompanyDialog: false
        });
    };

    render() {
        const props = this.props;
        const state = this.state;
        return (<>
            <VerticalContainer
                before={<>
                    <ActionContainer
                        leftItems={
                            <ListSearchFilter
                                searchFilter={state.filter}
                                onSearchClick={this.changeFilter} />
                        }
                        rightItems={
                            <ListIconButtonsItem
                                addTooltip={Translations.AddPartner}
                                removeTooltip={Translations.RemovePartner}
                                addDisabled={props.isReadOnly}
                                removeDisabled={!state.selectedId && state.checkedIds.length < 1}
                                onAdd={this.handleAdd}
                                onRemove={this.handleRemove}
                            />
                        }
                    />
                    {!!props.isReadOnly &&
                        <Alert severity="info">{Translations.NewCompanyMustBeSavedBeforePartnersCanBeAdded}</Alert>
                    }
                </>}
                main={<>
                    {!props.isReadOnly &&
                        <List
                            intialized={true}
                            isLoading={state.isLoading}
                            hasMore={false}
                            globalLoadingInUse={false}
                            header={{
                                columns: [
                                    { size: 5, column: "code", content: Translations.Code },
                                    { size: 5, column: "name", content: Translations.Name },
                                    { size: 2, column: "activeState", content: Translations.ActiveState },
                                ]
                            }}
                            lines={
                                state.companies.map((company) => {
                                    return {
                                        columns: [
                                            {
                                                size: 5,
                                                column: "code",
                                                content:
                                                    <>
                                                        <AvatarImage
                                                            marginRight=".5rem"
                                                            name={company.name}
                                                            image={company.logo}
                                                            tooltip={company.name}
                                                        />
                                                        <EllipsisTypography>{company.code}</EllipsisTypography>
                                                    </>
                                            },
                                            { size: 5, column: "name", content: company.name },
                                            { size: 2, column: "activeState", content: company.activeStateStr },
                                        ],
                                        id: company.id,
                                        classes: EnumHelper.isEqual(company.activeState, CompanyActiveState.NotInUse) ? "disabled" : ""
                                    };
                                })
                            }
                            noDataMessage={Translations.AddPartnerCompanies}
                            sortColumn={state.sortColumn}
                            sortOrderIsAsc={state.sortOrderIsAsc}
                            selectedId={state.selectedId}
                            checkedIds={state.checkedIds}
                            onSetSelectedIds={this.handleSetSelectedIds}
                            onSearchLines={this.handleSearchLines}
                        />
                    }
                </>}
            />
            {state.showSelectCompanyDialog &&
                <SelectCompanyDialog
                    multiSelect={true}
                    ignoreCompanyIds={props.companies.map(i => i.id).concat([props.companyId])}
                    ignoreCompanyGroupId={props.allPartnersCompanyGroupId}
                    onOk={this.onSelectCompanyDialogOk}
                    onCancel={this.onSelectCompanyDialogCancel}
                />
            }
        </>);
    }
}